import React, { RefCallback, useCallback, useRef } from 'react'
import { Route, useRouteMatch } from 'react-router-dom'
import { connect } from 'react-redux'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import config from 'constants/config'
import LineRssIcon from 'LuxKit/Icons/line/LineRssIcon'
import PinterestIcon from 'LuxKit/Icons/custom/PinterestIcon'
import LineInstagramIcon from 'LuxKit/Icons/line/LineInstagramIcon'
import LineFacebookIcon from 'LuxKit/Icons/line/LineFacebookIcon'
import DestinationsCarousels from 'components/Common/DestinationsCarousel/DestinationCarousels'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import TextLink from 'LuxKit/TextLink'
import TopDestinationList from 'components/Common/TopDestinationsList/TopDestinationList'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'
import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import SvgIcon from 'LuxKit/SvgIcon'
import { useAppFooterObserver } from 'hooks/useAppLayoutObservers'

const Container = styled.div`
  padding: ${rem(16)} 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};

  @media print {
    /* Hide footer entirely in print */
    display: none;

    a[href]:after {
      content: "";
    }
  }
`

const AppLogo = styled(Image)`
  max-width: 100%;
`

const AppLogoLink = styled(ClickableLink)`
  width: ${rem(128)};
`

const TopSectionWrapper = styled.div`
  padding: ${rem(32)} 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  text-align: center;

  ${mediaQueryUp.tablet} {
    text-align: left;
  }
`

interface SocialLink {
  href: string
  Icon: typeof SvgIcon
}

const SUPPORT_PAGE_ROUTE = '/:regionCode/support'

function CompactFooter() {
  const intl = useIntl()
  const currentYear = new Date().getFullYear()
  const containerRef = useRef<HTMLDivElement>(null)
  useAppFooterObserver(containerRef)
  const [footerRef, footerInView] = useInView({
    triggerOnce: true, // Load it once and keep it loaded
    rootMargin: '500px', // Trigger 500px before the component is reached
  })
  const combinedRefCallback = useCallback<RefCallback<HTMLDivElement>>((instance) => {
    footerRef(instance)
    containerRef.current = instance
  }, [footerRef])

  const whiteLabelAppConfig = useWhiteLabelAppConfig()
  const isSupportPage = !!useRouteMatch({ path: SUPPORT_PAGE_ROUTE })

  const socialLinks = (() => {
    const links = new Map<string, SocialLink>()
    if (config.footer.links?.facebook) links.set('facebook', { href: config.footer.links.facebook, Icon: LineFacebookIcon })
    if (config.footer.links?.instagram) links.set('instagram', { href: config.footer.links.instagram, Icon: LineInstagramIcon })
    // we really did misspell pinterest huh...
    if (config.footer.links?.pintrest) links.set('pinterest', { href: config.footer.links.pintrest, Icon: PinterestIcon })
    if (config.footer.links?.blog) links.set('blog', { href: config.footer.links.blog, Icon: LineRssIcon })
    return Array.from(links.entries())
  })()

  const followUsTitle = intl.formatMessage({
    id: 'lC8+BswwIl',
    defaultMessage: 'Follow Us',
    description: 'Title for social media links section in compact footer',
  })

  const downloadAppTitle = intl.formatMessage({
    id: '8iWou6kkYX',
    defaultMessage: 'Download our free app',
    description: 'Title for mobile app download section in compact footer',
  })

  const homeLink = intl.formatMessage({
    id: 'xS24afuaQV',
    defaultMessage: 'Home',
    description: 'Home link text in compact footer',
  })

  const faqsLink = intl.formatMessage({
    id: '5ZngSmqeBP',
    defaultMessage: 'FAQs',
    description: 'FAQs link text in compact footer',
  })

  const copyrightText = intl.formatMessage(
    {
      id: 'hUdwoLHCGC',
      defaultMessage: '© {year} {title} - All Rights Reserved.',
      description: 'Copyright notice in compact footer with year and company title',
    },
    {
      year: currentYear,
      title: config.title,
    },
  )

  const termsAndConditionsLink = intl.formatMessage({
    id: 'D2pc/NJ6fy',
    defaultMessage: 'Terms & Conditions',
    description: 'Terms and Conditions link text in compact footer',
  })

  const privacyPolicyText = config.brandConfig.privacyPolicySynonym

  const privacyPolicyInfoText = intl.formatMessage(
    {
      id: 'LlBawLV7Y2',
      defaultMessage: 'To see how we manage your personal information and other details, please see our <link>{privacyPolicy}</link>.',
      description: 'Privacy policy information text with link in compact footer',
    },
    {
      privacyPolicy: privacyPolicyText,
      link: (chunks: React.ReactNode) => (
        <TextLink key="privacy-link" href="/privacy-policy">
          {chunks}
        </TextLink>
      ),
    },
  )

  return <Container ref={combinedRefCallback}>
    {footerInView && <TopSectionWrapper>
      <Route exact path="/:regionCode" component={DestinationsCarousels} />
      <Route exact path="/:regionCode" component={TopDestinationList} />
      <Route exact path="/:regionCode/(hotels|experiences|tours|flights)">
        <TopDestinationList destinationNavItems={['Cities', 'Popular', 'Countries']} />
      </Route>
    </TopSectionWrapper>}

    <LayoutContainer size="lg">
      <Group direction="vertical" gap={32}>
        {!whiteLabelAppConfig.isApp && !isSupportPage && <Group
          direction="vertical"
          desktopDirection="horizontal"
          horizontalAlign="center"
          desktopHorizontalAlign="space-between"
          verticalAlign="center"
          gap={32}
        >
          <Group direction="horizontal" gap={16}>
            <TextLink href="/au" size="large">{homeLink}</TextLink>
            {config.header.faq && <TextLink href={config.header.faq.href} size="large">{faqsLink}</TextLink>}
          </Group>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="center" gap={24} wrap="wrap">
            {!!socialLinks.length && <Group direction="vertical" horizontalAlign="center" gap={8}>
              <BodyText variant="large" align="center" format="uppercase" weight="bold">{followUsTitle}</BodyText>
              <Group direction="horizontal" horizontalAlign="center" gap={8}>
                {socialLinks.map(([key, socialLink]) => <IconButton
                  key={key}
                  href={socialLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  kind="primary"
                  shape="circle"
                >
                  <socialLink.Icon />
                </IconButton>)}
              </Group>
            </Group>}
            {!whiteLabelAppConfig.isApp && <Group direction="vertical" horizontalAlign="center" gap={8}>
              <BodyText variant="large" align="center" format="uppercase" weight="bold">{downloadAppTitle}</BodyText>
              <Group direction="horizontal" horizontalAlign="center" gap={8}>
                {!!config.footer.links?.ios && <AppLogoLink href={config.footer.links.ios} target="_blank" rel="noopener noreferrer"><AppLogo id="apple_jch2pt" alt="play-store-logo" /></AppLogoLink>}
                {!!config.footer.links?.android && <AppLogoLink href={config.footer.links.android} target="_blank" rel="noopener noreferrer"><AppLogo id="google_play_ytxm3z" alt="play-store-logo" /></AppLogoLink>}
              </Group>
            </Group>}
          </Group>
        </Group>}

        <Group direction="horizontal" horizontalAlign="center" desktopHorizontalAlign="start" gap={8} wrap="wrap">
          <BodyText variant="large">{copyrightText}</BodyText>
          <BodyText variant="large"><TextLink underline={false} href="/terms-and-conditions">{termsAndConditionsLink} </TextLink></BodyText>
          <BodyText variant="large"><TextLink underline={false} href="/privacy-policy">{privacyPolicyText}</TextLink></BodyText>
        </Group>

        <BodyText variant="large" align="center" desktopAlign="start">{privacyPolicyInfoText}</BodyText>

        <Group direction="horizontal" horizontalAlign="center" desktopHorizontalAlign="start" wrap="wrap" noDisplayWhenEmpty>
          {config.footer.partners.map(partner => <TextLink
            key={partner.name}
            target="_blank"
            rel="noopener noreferrer"
            href={partner.href}
            size="large"
          >
            {intl.formatMessage(
              {
                id: 'h5LyQY847i',
                defaultMessage: 'Powered by {partnerName}',
                description: 'Powered by partner text in compact footer',
              },
              {
                partnerName: partner.name,
              },
            )}
          </TextLink>)}
        </Group>
      </Group>
    </LayoutContainer>
  </Container>
}

function mapStateToProps(state: App.State) {
  return {
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(CompactFooter)
